import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  SITE,
  BUILD_DATE,
  createOrganizationSchema,
  createPlaceSchema,
  createBreadcrumbSchema,
  createFAQSchema,
  createHowToSchema,
  createArticleSchema,
  createTravelGuideSchema,
  createEntitySchema,
  createTouristDestinationSchema,
  createCoreGraph,
  createWebPageSchema,
  personId,
} from '@/lib/seo-utils';
import { defaultAuthor } from '@/data/author';
import { buscaPassagensWidget } from '@/lib/widgets';
import biodiversidade from '@/data/biodiversidade.json';
import passeiosTax from '@/data/passeios-taxonomia.json';
import trilhasPilar from '@/data/trilhas-pilar.json';
import passeiosBarcoPilar from '@/data/passeios-barco-pilar.json';
// @ts-ignore
import baresData from '@/data/bares.json';
import { useTranslation } from '@/hooks/useTranslation';
import { useI18n } from '@/hooks/useI18n';

const SUPPORTED_LANGS = ['pt-BR', 'es', 'en', 'de', 'fr'];
const LANG_PATHS: Record<string, string> = {
  'pt-BR': '',
  es: '/es',
  en: '/en',
  de: '/de',
  fr: '/fr',
};

export interface HreflangItem {
  lang: string;
  url: string;
}

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonical?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
  hreflangs?: HreflangItem[];
}

export function SEO({
  title,
  description,
  keywords,
  ogImage = '/images/praia-do-farol-hero.jpg',
  ogType = 'website',
  canonical,
  jsonLd,
  noindex = false,
  hreflangs,
}: SEOProps) {
  const { lang } = useI18n();
  const fullTitle = title.includes(SITE.name) ? title : `${title} | ${SITE.name}`;
  const fullUrl = canonical ? `${SITE.url}${canonical}` : SITE.url;
  const fullImage = ogImage.startsWith('http') ? ogImage : `${SITE.url}${ogImage}`;

  // Gera hreflang automático se canonical for fornecido
  const autoHreflangs = React.useMemo(() => {
    if (!canonical) return [];
    return SUPPORTED_LANGS.map(l => ({
      lang: l === 'pt-BR' ? 'pt-BR' : l,
      url: `${SITE.url}${LANG_PATHS[l]}${canonical}`,
    }));
  }, [canonical]);
  const allHreflangs = hreflangs || autoHreflangs;

  const defaultJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TravelGuide',
    name: fullTitle,
    description,
    url: fullUrl,
    image: fullImage,
    publisher: createOrganizationSchema(),
  };

  const structuredData = jsonLd || defaultJsonLd;

  return (
    <Helmet htmlAttributes={{ lang: lang === 'pt-BR' ? 'pt-BR' : lang }}>
      {/* Basic */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && (
        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
      )}

      {/* Canonical */}
      <link rel="canonical" href={fullUrl} />

      {/* Hreflang */}
      {allHreflangs?.map(h => (
        <link key={h.lang} rel="alternate" hrefLang={h.lang} href={h.url} />
      ))}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content={SITE.locale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* Structured Data */}
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
}

/* ═══════════════════════════════════════════
   PRE-CONFIGURED SEO — Page presets
   ═══════════════════════════════════════════ */

const getHomeFaqSchema = (t: (key: string) => string) =>
  Array.from({ length: 8 }, (_, i) => ({
    q: t(`home.faq.items.${i}.q`),
    a: t(`home.faq.items.${i}.a`),
  }));

export const PrevisaoDoTempoSEO = () => {
  const { t } = useTranslation();
  const { lang } = useI18n();
  const canonical = '/previsao-do-tempo';
  const imageUrl = `${SITE.url}/images/sailboat.jpg`;

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: t('seo.weather.title'),
      description: t('seo.weather.description'),
      url: `${SITE.url}${canonical}`,
      image: imageUrl,
      datePublished: '2026-04-24',
      dateModified: BUILD_DATE,
      author: createOrganizationSchema(),
      publisher: createOrganizationSchema(),
      inLanguage: lang,
    },
    createFAQSchema([
      { q: t('seo.schema.home.faq.6.q'), a: t('seo.schema.home.faq.6.a') },
      { q: t('weather.faq.0.q'), a: t('weather.faq.0.a') },
      { q: t('weather.faq.1.q'), a: t('weather.faq.1.a') },
    ]),
    createBreadcrumbSchema([
      { name: t('seo.schema.home.breadcrumbHome'), path: '/' },
      { name: t('seo.weather.breadcrumb'), path: canonical },
    ]),
  ];

  return (
    <SEO
      title={t('seo.weather.title')}
      description={t('seo.weather.description')}
      keywords={t('seo.weather.keywords')}
      canonical={canonical}
      ogImage="/images/sailboat.jpg"
      jsonLd={schemas}
    />
  );
};

export const HomeSEO = () => {
  const { t } = useTranslation();
  const { lang } = useI18n();
  const imageUrl = `${SITE.url}/images/praia-do-farol-hero.jpg`;
  const canonical = '/';

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'TravelGuide',
      name: t('seo.schema.home.travelGuideName'),
      description: t('seo.schema.home.travelGuideDescription'),
      url: SITE.url,
      image: imageUrl,
      publisher: createOrganizationSchema(),
      about: createPlaceSchema(
        t('seo.schema.home.aboutName'),
        t('seo.schema.home.aboutDescription'),
        -25.5167,
        -48.3167
      ),
      inLanguage: lang,
    },
    createTouristDestinationSchema({
      name: t('seo.schema.home.destinationName'),
      description: t('seo.schema.home.destinationDescription'),
      url: SITE.url,
      image: imageUrl,
      lat: -25.5167,
      lng: -48.3167,
      containedInPlace: { name: 'Paraná', addressCountry: 'BR' },
      containsPlace: [
        { name: t('seo.schema.home.containsPlace.0') },
        { name: t('seo.schema.home.containsPlace.1') },
        { name: t('seo.schema.home.containsPlace.2') },
        { name: t('seo.schema.home.containsPlace.3') },
      ],
      touristType: [
        t('seo.schema.home.touristType.0'),
        t('seo.schema.home.touristType.1'),
        t('seo.schema.home.touristType.2'),
      ],
    }),
    createBreadcrumbSchema([
      { name: t('seo.schema.home.breadcrumbHome'), path: '/' },
      { name: t('seo.schema.home.destinationName'), path: canonical },
    ]),
    // A home agora É o guia completo (guia consolidado via 301): herda o schema rico.
    createArticleSchema(
      {
        headline: t('seo.schema.guide.articleHeadline'),
        description: t('seo.schema.guide.articleDescription'),
        url: SITE.url,
        image: imageUrl,
        datePublished: '2026-04-22',
        wordCount: 4500,
      },
      lang
    ),
    // HowTo dos roteiros — âncoras (#como-chegar, #roteiros, ...) agora vivem na home.
    ...getGuiaHowToSchema(t),
    // FAQ completa do guia (é a renderizada na home).
    createFAQSchema(getGuiaFaqSchema(t)),
    // Widget de passagens — schema reutilizável.
    buscaPassagensWidget.getSchema('https://www.vaidepromo.com.br/passagens-aereas/pesquisa'),
  ];

  return (
    <SEO
      title={t('seo.home.title')}
      description={t('seo.home.description')}
      keywords={t('seo.home.keywords')}
      canonical={canonical}
      jsonLd={schemas}
    />
  );
};

/* ─── Guia Completo — SEO enriquecido ─── */

const getGuiaFaqSchema = (t: (key: string) => string) => [
  {
    q: t('seo.schema.faq.0.q'),
    a: t('seo.schema.faq.0.a'),
  },
  {
    q: t('seo.schema.faq.1.q'),
    a: t('seo.schema.faq.1.a'),
  },
  {
    q: t('seo.schema.faq.2.q'),
    a: t('seo.schema.faq.2.a'),
  },
  {
    q: t('seo.schema.faq.3.q'),
    a: t('seo.schema.faq.3.a'),
  },
  {
    q: t('seo.schema.faq.4.q'),
    a: t('seo.schema.faq.4.a'),
  },
  {
    q: t('seo.schema.faq.5.q'),
    a: t('seo.schema.faq.5.a'),
  },
  {
    q: t('seo.schema.faq.6.q'),
    a: t('seo.schema.faq.6.a'),
  },
  {
    q: t('seo.schema.faq.7.q'),
    a: t('seo.schema.faq.7.a'),
  },
  {
    q: t('seo.schema.faq.8.q'),
    a: t('seo.schema.faq.8.a'),
  },
  {
    q: t('seo.schema.faq.9.q'),
    a: t('seo.schema.faq.9.a'),
  },
  {
    q: t('seo.schema.faq.10.q'),
    a: t('seo.schema.faq.10.a'),
  },
  {
    q: t('seo.schema.faq.11.q'),
    a: t('seo.schema.faq.11.a'),
  },
  {
    q: t('seo.schema.faq.12.q'),
    a: t('seo.schema.faq.12.a'),
  },
  {
    q: t('seo.schema.faq.13.q'),
    a: t('seo.schema.faq.13.a'),
  },
  {
    q: t('seo.schema.faq.14.q'),
    a: t('seo.schema.faq.14.a'),
  },
];

const getGuiaHowToSchema = (t: (key: string) => string) => [
  createHowToSchema(
    t('seo.schema.howTo.1day.name'),
    t('seo.schema.howTo.1day.description'),
    'PT8H',
    [
      {
        name: t('seo.schema.howTo.1day.steps.0.name'),
        text: t('seo.schema.howTo.1day.steps.0.text'),
        url: 'https://vemprailhadomel.com.br/#como-chegar',
      },
      {
        name: t('seo.schema.howTo.1day.steps.1.name'),
        text: t('seo.schema.howTo.1day.steps.1.text'),
        url: 'https://vemprailhadomel.com.br/#atracoes',
      },
      {
        name: t('seo.schema.howTo.1day.steps.2.name'),
        text: t('seo.schema.howTo.1day.steps.2.text'),
        url: 'https://vemprailhadomel.com.br/#atracoes',
      },
      {
        name: t('seo.schema.howTo.1day.steps.3.name'),
        text: t('seo.schema.howTo.1day.steps.3.text'),
        url: 'https://vemprailhadomel.com.br/#onde-ficar',
      },
      {
        name: t('seo.schema.howTo.1day.steps.4.name'),
        text: t('seo.schema.howTo.1day.steps.4.text'),
        url: 'https://vemprailhadomel.com.br/#atracoes',
      },
      {
        name: t('seo.schema.howTo.1day.steps.5.name'),
        text: t('seo.schema.howTo.1day.steps.5.text'),
        url: 'https://vemprailhadomel.com.br/#como-chegar',
      },
    ],
    { currency: 'BRL', value: '110' }
  ),
  createHowToSchema(
    t('seo.schema.howTo.3days.name'),
    t('seo.schema.howTo.3days.description'),
    'PT3D',
    [
      {
        name: t('seo.schema.howTo.3days.steps.0.name'),
        text: t('seo.schema.howTo.3days.steps.0.text'),
        url: 'https://vemprailhadomel.com.br/#roteiros',
      },
      {
        name: t('seo.schema.howTo.3days.steps.1.name'),
        text: t('seo.schema.howTo.3days.steps.1.text'),
        url: 'https://vemprailhadomel.com.br/#roteiros',
      },
      {
        name: t('seo.schema.howTo.3days.steps.2.name'),
        text: t('seo.schema.howTo.3days.steps.2.text'),
        url: 'https://vemprailhadomel.com.br/#roteiros',
      },
    ],
    { currency: 'BRL', value: '1200-2000' }
  ),
];

export const GuiaCompletoSEO = () => {
  const { t } = useTranslation();
  const { lang } = useI18n();
  const pageUrl = `${SITE.url}/guia-completo`;
  const imageUrl = `${SITE.url}/images/praia-do-farol-hero.jpg`;

  const title = t(
    'seo.guide.title',
    'Guia Completo da Ilha do Mel 2026 | Roteiros, Praias e Onde Ficar'
  );
  const description = t(
    'seo.guide.description',
    'Planeje sua viagem com o guia mais completo da Ilha do Mel. 40+ atrações, roteiros de 1 a 3 dias, mapa interativo e dicas de onde ficar e comer. Atualizado 2026.'
  );

  const baseKeywords = t('seo.schema.guide.keywords');
  const enrichedKeywords = buscaPassagensWidget.getKeywords(baseKeywords);

  const schemas = [
    createTravelGuideSchema(
      {
        name: t('seo.schema.guide.travelGuideName'),
        description: t('seo.schema.guide.travelGuideDescription'),
        url: pageUrl,
        image: imageUrl,
        datePublished: '2026-04-22',
        about: createPlaceSchema(
          t('seo.schema.guide.aboutName'),
          t('seo.schema.guide.aboutDescription'),
          -25.5167,
          -48.3167
        ),
      },
      lang
    ),
    createArticleSchema(
      {
        headline: t('seo.schema.guide.articleHeadline'),
        description: t('seo.schema.guide.articleDescription'),
        url: pageUrl,
        image: imageUrl,
        datePublished: '2026-04-22',
        wordCount: 4500,
      },
      lang
    ),
    createFAQSchema(getGuiaFaqSchema(t)),
    ...getGuiaHowToSchema(t),
    createBreadcrumbSchema([
      { name: t('seo.schema.guide.breadcrumbHome'), path: '/' },
      { name: t('seo.schema.guide.breadcrumbGuide'), path: '/guia-completo' },
    ]),
    // Schema do widget de passagens — SEO reutilizável
    buscaPassagensWidget.getSchema('https://www.vaidepromo.com.br/passagens-aereas/pesquisa'),
  ];

  return (
    <SEO
      title={title}
      description={description}
      keywords={enrichedKeywords}
      canonical="/guia-completo"
      ogType="article"
      jsonLd={{ '@context': 'https://schema.org', '@graph': schemas }}
    />
  );
};

export const FAQSchemaGuia = () => null; // Schema agora está em GuiaCompletoSEO

/* ─── Outras páginas ─── */

export const ExperienciasSEO = () => {
  const { t } = useTranslation();
  return (
    <SEO
      title={t('seo.experiences.title')}
      description={t('seo.experiences.description')}
      keywords={t('seo.experiences.keywords')}
      canonical="/experiencias"
      ogType="article"
    />
  );
};

export const OQueFazerSEO = () => {
  const canonical = '/o-que-fazer-ilha-do-mel';
  const description =
    'Guia completo do que fazer na Ilha do Mel: passeios de barco, trilhas, esportes e experiências, com preços e um quiz pra achar o passeio da sua cara.';

  const itens = (passeiosTax.subtipos as any[]).flatMap((s: any) =>
    (s.itens || []).map((it: any) => ({ ...it, subKey: s.key }))
  );

  const schemas = [
    {
      '@type': 'ItemList',
      name: 'O que fazer na Ilha do Mel',
      description,
      url: `${SITE.url}${canonical}`,
      numberOfItems: itens.length,
      itemListElement: itens.map((it: any, i: number) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'TouristAttraction',
          name: it.titulo,
          description: it.blurb,
          url:
            it.link && it.link.startsWith('/')
              ? `${SITE.url}${it.link}`
              : `${SITE.url}${canonical}#subtipo-${it.subKey}`,
        },
      })),
    },
    createFAQSchema((passeiosTax.faq as any[]).map((f: any) => ({ q: f.q, a: f.a }))),
    createBreadcrumbSchema([
      { name: 'Início', path: '/' },
      { name: 'O que fazer na Ilha do Mel', path: canonical },
    ]),
  ];

  return (
    <SEO
      title="O que fazer na Ilha do Mel: passeios, trilhas e atividades 2026"
      description={description}
      keywords="o que fazer na ilha do mel, passeios ilha do mel, passeio de barco ilha do mel, atividades ilha do mel, o que fazer ilha do mel"
      canonical={canonical}
      ogImage="/images/farol-card.jpg"
      jsonLd={{ '@context': 'https://schema.org', '@graph': schemas }}
    />
  );
};

export const BaresSEO = () => {
  const canonical = '/bares-ilha-do-mel';
  const description =
    'Guia honesto dos bares na Ilha do Mel: onde tem música ao vivo, forró, hambúrguer e cerveja, o que esperar da vida noturna (sem balada) e até que horas vai.';

  // Perguntas do pilar — alimentam o FAQPage do schema
  const faqItems = [
    {
      q: 'A Ilha do Mel tem balada?',
      a: 'Não no sentido de balada de cidade. A ilha é rústica, sem carros e sem grandes casas noturnas — o agito acontece em bares com música ao vivo, sobretudo em fins de semana e na alta temporada, divididos entre Encantadas e Nova Brasília.',
    },
    {
      q: 'Até que horas funciona a noite na ilha?',
      a: 'A música ao vivo é regulada: deve ser acústica, com limite por volta da meia-noite em dias de semana e até 2h em fins de semana e feriados. Fora disso, os bares seguem servindo, mas o movimento cai bem depois desse horário.',
    },
    {
      q: 'Tem música ao vivo? Que estilos?',
      a: 'Tem. O forró pé-de-serra reina no Forró do Wando, em Nova Brasília; o Cavalo Marinho e o Stazione, em Encantadas, alternam reggae, MPB, rock e karaokê. A programação é irregular fora da temporada — vale confirmar no dia.',
    },
    {
      q: 'Os bares aceitam cartão ou preciso levar dinheiro?',
      a: 'Leve dinheiro e/ou pix. Alguns aceitam cartão, mas os bares menores costumam não passar, e o sinal de internet oscila o tempo todo. Não há caixa eletrônico na ilha, então saque antes de atravessar.',
    },
    {
      q: 'É caro? Quanto custa a cerveja?',
      a: 'A cerveja gira em torno de R$25 (varia por casa e temporada). No geral, os preços da ilha são um pouco mais altos que no continente, porque tudo chega de barca. Petiscos e porções também.',
    },
    {
      q: 'Onde tem mais movimento à noite, Encantadas ou Nova Brasília?',
      a: 'As duas concentram a vida noturna, cada uma com seus points. Encantadas tem Cavalo Marinho, Stazione e as hamburguerias; Nova Brasília tem o forró e bares pé na areia. Como atravessar de vila à noite é difícil, o mais prático é curtir onde você dorme.',
    },
    {
      q: 'É seguro voltar à noite? Preciso de lanterna?',
      a: 'A ilha é tranquila, mas não há iluminação pública nas trilhas e caminhos de areia. Leve lanterna ou use a do celular — é indispensável para voltar ao alojamento no escuro com segurança.',
    },
    {
      q: 'Tem hamburgueria e cerveja artesanal?',
      a: 'Sim, em Encantadas. O Madruga Burger Beer é temático do Seu Madruga e bem avaliado; o Ilha do Malte foca em cervejas artesanais e no hambúrguer de costela. Boa alternativa a quem quer fugir dos frutos do mar.',
    },
    {
      q: 'Tem forró? Onde?',
      a: 'O forró ao vivo rola no Forró do Wando, em Nova Brasília, perto do Toca do Abutre — às vezes com bandas nativas da ilha. Não tem redes sociais nem horário fixo, então pergunte a moradores ou à sua pousada se vai ter forró na noite.',
    },
    {
      q: 'Funciona fora de temporada ou em dia de semana?',
      a: 'Sim, mas com muito menos movimento e programação. Fora da alta temporada e nos dias de semana, vários bares reduzem os shows e podem fechar mais cedo. Confirme antes de sair, principalmente no inverno.',
    },
  ];

  const bares = baresData as any[];

  const schemas = [
    {
      '@type': 'ItemList',
      name: 'Bares na Ilha do Mel',
      description,
      url: `${SITE.url}${canonical}`,
      numberOfItems: bares.length,
      itemListElement: bares.map((b: any, i: number) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': b.schemaOrg?.['@type'] === 'Restaurant' ? 'Restaurant' : 'TouristAttraction',
          name: b.title,
          description: b.subtitle,
          url: `${SITE.url}/bar/${b.handle}`,
          ...(b.image && { image: `${SITE.url}${b.image}` }),
          ...(b.faixaPreco && { priceRange: b.faixaPreco }),
          address: {
            '@type': 'PostalAddress',
            addressLocality: b.vilarejoName,
            addressRegion: 'PR',
            addressCountry: 'BR',
          },
        },
      })),
    },
    createFAQSchema(faqItems),
    createBreadcrumbSchema([
      { name: 'Início', path: '/' },
      { name: 'Bares na Ilha do Mel', path: canonical },
    ]),
  ];

  return (
    <SEO
      title="Bares na Ilha do Mel 2026: os melhores para beber, comer e a vida noturna"
      description={description}
      keywords="bares ilha do mel, vida noturna ilha do mel, o que fazer à noite ilha do mel, música ao vivo ilha do mel, forró ilha do mel, balada ilha do mel"
      canonical={canonical}
      ogImage="/images/gruta-encantadas-noite.jpg"
      jsonLd={{ '@context': 'https://schema.org', '@graph': schemas }}
    />
  );
};

export const OndeFicarSEO = () => {
  const { t } = useTranslation();
  return (
    <SEO
      title={t('seo.whereToStay.title')}
      description={t('seo.whereToStay.description')}
      keywords={t('seo.whereToStay.keywords')}
      canonical="/pousadas"
    />
  );
};

export const CategorySEO = ({
  category,
  canonicalPath,
}: {
  category: string;
  canonicalPath?: string;
}) => {
  const { t } = useTranslation();
  return (
    <SEO
      title={t(`seo.category.${category}.title`)}
      description={t(`seo.category.${category}.description`)}
      keywords={t(`seo.category.${category}.keywords`)}
      canonical={canonicalPath || `/${category}`}
    />
  );
};

export const PraiasSEO = ({ praias }: { praias: any[] }) => {
  const { t } = useTranslation();
  const canonical = '/praias-ilha-do-mel';

  const faqItems = Array.from({ length: 8 }, (_, i) => ({
    q: t(`page.praias.faq.items.${i}.q`),
    a: t(`page.praias.faq.items.${i}.a`),
  }));

  const schemas = [
    {
      '@type': 'ItemList',
      name: 'Praias da Ilha do Mel',
      description: t('page.praias.seo.description'),
      url: `${SITE.url}${canonical}`,
      numberOfItems: praias.length,
      itemListElement: praias.map((praia: any, i: number) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Beach',
          name: praia.title,
          description: praia.subtitle,
          url: `${SITE.url}/praias/${praia.handle}`,
          ...(praia.image && { image: `${SITE.url}${praia.image}` }),
          ...(praia.schemaOrg?.geo && {
            geo: {
              '@type': 'GeoCoordinates',
              latitude: praia.schemaOrg.geo.latitude,
              longitude: praia.schemaOrg.geo.longitude,
            },
          }),
        },
      })),
    },
    createFAQSchema(faqItems),
    createBreadcrumbSchema([
      { name: 'Início', path: '/' },
      { name: 'Praias da Ilha do Mel', path: canonical },
    ]),
  ];

  return (
    <SEO
      title={t('page.praias.seo.title')}
      description={t('page.praias.seo.description')}
      keywords={t('page.praias.seo.keywords')}
      canonical={canonical}
      ogImage="/images/hero-bg.jpg"
      jsonLd={{ '@context': 'https://schema.org', '@graph': schemas }}
    />
  );
};

export const PontosTuristicosSEO = ({ atracoes }: { atracoes: any[] }) => {
  const canonical = '/pontos-turisticos-ilha-do-mel';
  const description =
    'Todos os pontos turísticos da Ilha do Mel: Farol das Conchas, Fortaleza, Gruta das Encantadas, Baía dos Golfinhos e mais. O que são, como chegar e a melhor época de cada um.';

  const faqItems = [
    {
      q: 'Quais são os principais pontos turísticos da Ilha do Mel?',
      a: 'Os mais visitados são o Farol das Conchas, a Fortaleza de Nossa Senhora dos Prazeres, a Gruta das Encantadas, o Istmo (Passa-Passa) e a Baía dos Golfinhos. Todos são gratuitos, e a maioria é acessível por trilhas curtas a partir das vilas de Nova Brasília e Encantadas.',
    },
    {
      q: 'Precisa pagar para visitar os pontos turísticos da ilha?',
      a: 'A grande maioria é gratuita — Farol, Fortaleza, Gruta, Morro do Sabão, Istmo e Ponta Oeste não cobram entrada. Só há custo quando o acesso depende de passeio de barco, como a Baía dos Golfinhos.',
    },
    {
      q: 'Qual a melhor época para conhecer os pontos turísticos?',
      a: 'O ano todo funciona, mas o verão (dezembro a março) tem mar mais quente e dias ensolarados; primavera e outono trazem trilhas mais tranquilas e menos calor. Atrações como a Gruta das Encantadas dependem da maré baixa (geralmente pela manhã).',
    },
    {
      q: 'Quantos degraus tem o Farol das Conchas?',
      a: 'São cerca de 150 degraus até o topo, precedidos de uma caminhada plana de uns 30 minutos desde Nova Brasília. Não se entra na torre do farol, mas o mirante externo tem vista de 360° da ilha inteira.',
    },
    {
      q: 'O Farol das Conchas é grátis? Dá para subir?',
      a: 'Sim, a visita é gratuita. Você sobe os ~150 degraus até a base do farol (a torre em si não é aberta ao público), de onde se tem a melhor vista da Ilha do Mel.',
    },
    {
      q: 'Como saber a maré para visitar a Gruta das Encantadas?',
      a: 'A Gruta só pode ser visitada e adentrada na maré baixa, geralmente pela manhã (por volta das 8h às 13h). Consulte a tábua de maré da Ilha do Mel antes de ir — na maré alta, a água cobre a entrada.',
    },
    ...biodiversidade.faq,
  ];

  const schemas = [
    {
      '@type': 'ItemList',
      name: 'Pontos Turísticos da Ilha do Mel',
      description,
      url: `${SITE.url}${canonical}`,
      numberOfItems: atracoes.length,
      itemListElement: atracoes.map((a: any, i: number) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'TouristAttraction',
          name: a.title,
          description: a.subtitle,
          url: `${SITE.url}/ponto-turistico/${a.handle}`,
          ...(a.image && { image: `${SITE.url}${a.image}` }),
          ...(a.schemaOrg?.geo && {
            geo: {
              '@type': 'GeoCoordinates',
              latitude: a.schemaOrg.geo.latitude,
              longitude: a.schemaOrg.geo.longitude,
            },
          }),
        },
      })),
    },
    createFAQSchema(faqItems),
    createBreadcrumbSchema([
      { name: 'Início', path: '/' },
      { name: 'Pontos Turísticos da Ilha do Mel', path: canonical },
    ]),
  ];

  return (
    <SEO
      title="Pontos Turísticos da Ilha do Mel: o que visitar (guia 2026)"
      description={description}
      keywords="pontos turísticos ilha do mel, o que visitar ilha do mel, atrações ilha do mel, farol das conchas, gruta das encantadas, fortaleza ilha do mel"
      canonical={canonical}
      ogImage="/images/morro-farol-vista.jpg"
      jsonLd={{ '@context': 'https://schema.org', '@graph': schemas }}
    />
  );
};

export const TrilhasSEO = ({ trilhas }: { trilhas: any[] }) => {
  const canonical = '/trilhas-ilha-do-mel';
  const description =
    'As 6 trilhas da Ilha do Mel comparadas: distância, tempo, dificuldade e maré de cada rota — do passeio de 15 minutos à volta completa de 20 km, com um quiz pra escolher a sua.';

  const schemas = [
    {
      '@type': 'ItemList',
      name: 'Trilhas da Ilha do Mel',
      description,
      url: `${SITE.url}${canonical}`,
      numberOfItems: trilhas.length,
      itemListElement: trilhas.map((t: any, i: number) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'TouristTrip',
          name: t.title,
          description: t.subtitle,
          url: `${SITE.url}/trilha/${t.handle}`,
          ...(t.image && { image: `${SITE.url}${t.image}` }),
          touristType: t.kidsFriendly ? 'family, hiking' : 'hiking',
          additionalProperty: [
            t.distance && { '@type': 'PropertyValue', name: 'Distância', value: t.distance },
            t.duration && { '@type': 'PropertyValue', name: 'Duração', value: t.duration },
            t.difficulty && { '@type': 'PropertyValue', name: 'Dificuldade', value: t.difficulty },
          ].filter(Boolean),
        },
      })),
    },
    createFAQSchema((trilhasPilar.faq as any[]).map((f: any) => ({ q: f.q, a: f.a }))),
    createBreadcrumbSchema([
      { name: 'Início', path: '/' },
      { name: 'O que fazer na Ilha do Mel', path: '/o-que-fazer-ilha-do-mel' },
      { name: 'Trilhas da Ilha do Mel', path: canonical },
    ]),
  ];

  return (
    <SEO
      title="Trilhas da Ilha do Mel: as 6 rotas com tempo, distância e nível"
      description={description}
      keywords="trilhas ilha do mel, trilha da fortaleza ilha do mel, trilha do farol ilha do mel, caminhadas ilha do mel, trilha gruta das encantadas"
      canonical={canonical}
      ogImage="/images/trilha-floresta.jpg"
      jsonLd={{ '@context': 'https://schema.org', '@graph': schemas }}
    />
  );
};

export const PasseiosBarcoSEO = ({ nauticos }: { nauticos: any[] }) => {
  const canonical = '/passeios-de-barco-ilha-do-mel';
  const description =
    'Passeios de barco na Ilha do Mel a partir de R$80: Baía dos Golfinhos, volta na ilha, lancha exclusiva, pôr do sol na Ponta Oeste, jet ski e táxi náutico — rotas, preços e como reservar.';

  const schemas = [
    {
      '@type': 'ItemList',
      name: 'Passeios de Barco na Ilha do Mel',
      description,
      url: `${SITE.url}${canonical}`,
      numberOfItems: nauticos.length,
      itemListElement: nauticos.map((p: any, i: number) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'TouristTrip',
          name: p.title,
          description: p.subtitle,
          url: `${SITE.url}/passeio/${p.handle}`,
          ...(p.image && { image: `${SITE.url}${p.image}` }),
          ...(p.price?.amount && {
            offers: {
              '@type': 'Offer',
              price: String(p.price.amount),
              priceCurrency: 'BRL',
              description: p.price.unit,
            },
          }),
        },
      })),
    },
    createFAQSchema((passeiosBarcoPilar.faq as any[]).map((f: any) => ({ q: f.q, a: f.a }))),
    createBreadcrumbSchema([
      { name: 'Início', path: '/' },
      { name: 'O que fazer na Ilha do Mel', path: '/o-que-fazer-ilha-do-mel' },
      { name: 'Passeios de Barco na Ilha do Mel', path: canonical },
    ]),
  ];

  return (
    <SEO
      title="Passeios de Barco na Ilha do Mel: rotas, preços e golfinhos"
      description={description}
      keywords="passeio de barco ilha do mel, passeio baía dos golfinhos ilha do mel, lancha ilha do mel, jet ski ilha do mel, táxi náutico ilha do mel"
      canonical={canonical}
      ogImage="/images/sailboat.jpg"
      jsonLd={{ '@context': 'https://schema.org', '@graph': schemas }}
    />
  );
};

export const PousadasSEO = ({ pousadas, top }: { pousadas: any[]; top: any[] }) => {
  const { t } = useTranslation();
  const canonical = '/pousadas-ilha-do-mel';

  const faqItems = Array.from({ length: 8 }, (_, i) => ({
    q: t(`page.pousadas.faq.items.${i}.q`),
    a: t(`page.pousadas.faq.items.${i}.a`),
  }));

  // aggregateRating só quando há nota E contagem REAIS na mesma plataforma (evita rich snippet falso)
  const aggRating = (p: any) => {
    const r = p.rating || {};
    const rc = p.reviewCount || {};
    if (r.tripAdvisor && rc.tripAdvisor)
      return { '@type': 'AggregateRating', ratingValue: r.tripAdvisor, bestRating: 5, ratingCount: rc.tripAdvisor };
    if (r.booking && rc.booking)
      return { '@type': 'AggregateRating', ratingValue: r.booking, bestRating: 10, ratingCount: rc.booking };
    return null;
  };

  const schemas = [
    {
      '@type': 'ItemList',
      name: 'Melhores pousadas da Ilha do Mel',
      description: t('page.pousadas.seo.description'),
      url: `${SITE.url}${canonical}`,
      numberOfItems: top.length,
      itemListElement: top.map((p: any, i: number) => {
        const ar = aggRating(p);
        return {
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'LodgingBusiness',
            name: p.title,
            description: p.subtitle,
            url: `${SITE.url}/pousada/${p.handle}`,
            ...(p.image && { image: `${SITE.url}${p.image}` }),
            address: {
              '@type': 'PostalAddress',
              addressLocality: p.vilarejoName,
              addressRegion: 'PR',
              addressCountry: 'BR',
            },
            ...(p.schemaOrg?.geo && {
              geo: {
                '@type': 'GeoCoordinates',
                latitude: p.schemaOrg.geo.latitude,
                longitude: p.schemaOrg.geo.longitude,
              },
            }),
            ...(ar && { aggregateRating: ar }),
          },
        };
      }),
    },
    createFAQSchema(faqItems),
    createBreadcrumbSchema([
      { name: 'Início', path: '/' },
      { name: 'Pousadas na Ilha do Mel', path: canonical },
    ]),
  ];

  return (
    <SEO
      title={t('page.pousadas.seo.title')}
      description={t('page.pousadas.seo.description')}
      keywords={t('page.pousadas.seo.keywords')}
      canonical={canonical}
      ogImage="/images/hero-v2.jpg"
      jsonLd={{ '@context': 'https://schema.org', '@graph': schemas }}
    />
  );
};

export const RestaurantesSEO = ({ restaurantes, top }: { restaurantes: any[]; top: any[] }) => {
  const { t } = useTranslation();
  const canonical = '/restaurantes-ilha-do-mel';

  const faqItems = Array.from({ length: 8 }, (_, i) => ({
    q: t(`page.restaurantes.faq.items.${i}.q`),
    a: t(`page.restaurantes.faq.items.${i}.a`),
  }));

  // aggregateRating só quando há nota E contagem REAIS na mesma plataforma (evita rich snippet falso)
  const aggRating = (p: any) => {
    const r = p.rating || {};
    const rc = p.reviewCount || {};
    if (r.tripAdvisor && rc.tripAdvisor)
      return { '@type': 'AggregateRating', ratingValue: r.tripAdvisor, bestRating: 5, ratingCount: rc.tripAdvisor };
    if (r.google && rc.google)
      return { '@type': 'AggregateRating', ratingValue: r.google, bestRating: 5, ratingCount: rc.google };
    return null;
  };

  const schemas = [
    {
      '@type': 'ItemList',
      name: 'Melhores restaurantes da Ilha do Mel',
      description: t('page.restaurantes.seo.description'),
      url: `${SITE.url}${canonical}`,
      numberOfItems: top.length,
      itemListElement: top.map((p: any, i: number) => {
        const ar = aggRating(p);
        return {
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'Restaurant',
            name: p.title,
            description: p.subtitle,
            url: `${SITE.url}/restaurante/${p.handle}`,
            ...(p.image && { image: `${SITE.url}${p.image}` }),
            ...(p.cuisine?.length && { servesCuisine: p.cuisine }),
            ...(p.faixaPreco && { priceRange: p.faixaPreco }),
            address: {
              '@type': 'PostalAddress',
              addressLocality: p.vilarejoName,
              addressRegion: 'PR',
              addressCountry: 'BR',
            },
            ...(ar && { aggregateRating: ar }),
          },
        };
      }),
    },
    createFAQSchema(faqItems),
    createBreadcrumbSchema([
      { name: 'Início', path: '/' },
      { name: 'Restaurantes na Ilha do Mel', path: canonical },
    ]),
  ];

  return (
    <SEO
      title={t('page.restaurantes.seo.title')}
      description={t('page.restaurantes.seo.description')}
      keywords={t('page.restaurantes.seo.keywords')}
      canonical={canonical}
      ogImage="/images/hero-v2.jpg"
      jsonLd={{ '@context': 'https://schema.org', '@graph': schemas }}
    />
  );
};

export const EntityDetailSEO = ({
  title,
  description,
  category,
  image,
  entity,
}: {
  title: string;
  description: string;
  category: string;
  image?: string;
  entity?: Record<string, any>;
}) => {
  const { t } = useTranslation();
  const { lang } = useI18n();
  const catMap: Record<string, string> = {
    praias: t('seo.entity.catLabel.praias'),
    trilhas: t('seo.entity.catLabel.trilhas'),
    atracoes: t('seo.entity.catLabel.atracoes'),
    pousadas: t('seo.entity.catLabel.pousadas'),
    restaurantes: t('seo.entity.catLabel.restaurantes'),
    passeios: t('seo.entity.catLabel.passeios'),
    morros: t('seo.entity.catLabel.morros'),
    vilas: t('seo.entity.catLabel.vilas'),
    ilhas: t('seo.entity.catLabel.ilhas'),
    trapiches: t('seo.entity.catLabel.trapiches'),
    bar: 'Bares',
    bares: 'Bares',
    // formas singulares (URLs como /pousada/, /trilha/) — evita o rótulo cair em "Lugar"
    praia: t('seo.entity.catLabel.praias'),
    trilha: t('seo.entity.catLabel.trilhas'),
    atracao: t('seo.entity.catLabel.atracoes'),
    'ponto-turistico': t('seo.entity.catLabel.atracoes'),
    'pontos-turisticos': t('seo.entity.catLabel.atracoes'),
    pousada: t('seo.entity.catLabel.pousadas'),
    restaurante: t('seo.entity.catLabel.restaurantes'),
    passeio: t('seo.entity.catLabel.passeios'),
    morro: t('seo.entity.catLabel.morros'),
    vila: t('seo.entity.catLabel.vilas'),
    ilha: t('seo.entity.catLabel.ilhas'),
    trapiche: t('seo.entity.catLabel.trapiches'),
  };

  const catLabel = catMap[category] || t('seo.entity.catLabel.fallback');
  const canonical = entity?.handle
    ? `/${category}/${entity.handle}`
    : `/${category}/${title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`;
  const canonicalUrl = `${SITE.url}${canonical}`;

  // Meta customizado da entidade tem prioridade (ex: praia otimizada)
  const seoTitle = entity?.seo?.metaTitle || `${title} — ${catLabel} ${t('seo.entity.suffix')}`;
  const seoDescription = entity?.seo?.metaDescription || description;
  const pageImage = image
    ? image.startsWith('http')
      ? image
      : `${SITE.url}${image}`
    : `${SITE.url}/images/praia-do-farol-hero.jpg`;

  const breadcrumbId = `${canonicalUrl}#breadcrumb`;
  const placeId = `${canonicalUrl}#place`;

  // Grafo linkado por @id: Organization + WebSite + Person (autor) + WebPage + ...
  const schemas: Record<string, unknown>[] = [...createCoreGraph(lang)];

  // WebPage amarra autor + breadcrumb + entidade
  schemas.push(
    createWebPageSchema(
      {
        url: canonicalUrl,
        name: seoTitle,
        description: seoDescription,
        image: pageImage,
        dateModified: BUILD_DATE,
        breadcrumbId,
        aboutId: entity ? placeId : undefined,
        authorSlug: defaultAuthor.slug,
      },
      lang
    )
  );

  // Breadcrumb — o nível de categoria aponta para a página pilar quando existe
  const PILAR: Record<string, string> = {
    praia: '/praias-ilha-do-mel', praias: '/praias-ilha-do-mel',
    pousada: '/pousadas-ilha-do-mel', pousadas: '/pousadas-ilha-do-mel',
    restaurante: '/restaurantes-ilha-do-mel', restaurantes: '/restaurantes-ilha-do-mel',
  };
  const categoryPath = PILAR[category] || `/${category}`;
  schemas.push(
    createBreadcrumbSchema(
      [
        { name: t('seo.schema.guide.breadcrumbHome'), path: '/' },
        { name: catLabel, path: categoryPath },
        { name: title, path: canonical },
      ],
      breadcrumbId
    )
  );

  // Schema principal da entidade (Beach/TouristAttraction) — ligado à WebPage
  if (entity) {
    const catSingular: Record<string, string> = {
      // formas plurais (URLs como /praias/)
      praias: 'praia', trilhas: 'trilha', atracoes: 'atracao',
      pousadas: 'pousada', restaurantes: 'restaurante', passeios: 'passeio',
      morros: 'morro', vilas: 'vila', ilhas: 'ilha', trapiches: 'trapiche',
      // formas singulares (URLs como /pousada/, /trilha/) — evita cair no fallback 'atracao'
      praia: 'praia', trilha: 'trilha', atracao: 'atracao',
      pousada: 'pousada', restaurante: 'restaurante', passeio: 'passeio',
      morro: 'morro', vila: 'vila', ilha: 'ilha', trapiche: 'trapiche',
    };
    const entitySchema = createEntitySchema(
      entity,
      catSingular[category] as any || 'atracao',
      canonicalUrl,
      lang
    );
    schemas.push(entitySchema);

    // Restaurante da pousada como entidade própria (diferencial SEO — ataca também "restaurante X")
    if (entity.restaurante?.nome) {
      const rest = entity.restaurante;
      schemas.push({
        '@type': 'Restaurant',
        '@id': `${canonicalUrl}#restaurant`,
        name: rest.nome,
        url: `${canonicalUrl}#restaurante`,
        ...(rest.descricao && { description: rest.descricao }),
        servesCuisine: rest.cozinha || ['Frutos do mar', 'Brasileira'],
        priceRange: rest.faixaPreco || '$$',
        address: {
          '@type': 'PostalAddress',
          addressLocality: entity.vilarejoName || 'Ilha do Mel',
          addressRegion: 'PR',
          addressCountry: 'BR',
        },
        ...(entity.schemaOrg?.geo && { geo: {
          '@type': 'GeoCoordinates',
          latitude: entity.schemaOrg.geo.latitude,
          longitude: entity.schemaOrg.geo.longitude,
        }}),
        ...(rest.rating?.tripAdvisor && rest.rating?.reviewCount && {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: rest.rating.tripAdvisor,
            bestRating: 5,
            reviewCount: rest.rating.reviewCount,
          },
        }),
        isPartOf: { '@id': `${canonicalUrl}#place` },
      });
    }
  }

  // FAQPage — quando a entidade tem FAQ próprio (ex: super páginas de praia)
  if (entity?.faq?.length) {
    schemas.push({
      ...createFAQSchema(entity.faq.map((f: any) => ({ q: f.pergunta, a: f.resposta }))),
      '@id': `${canonicalUrl}#faq`,
      author: { '@id': personId(defaultAuthor.slug) },
      publisher: { '@id': SITE.url + '/#organization' },
    });
  }

  return (
    <SEO
      title={seoTitle}
      description={seoDescription}
      ogImage={image}
      canonical={canonical}
      jsonLd={{ '@context': 'https://schema.org', '@graph': schemas }}
    />
  );
};
